import { useState } from 'react'
import {
    ContentClassifierMetadataView,
    ContentSearchParams,
    useGetContentClassifierMetadataQuery,
    useGetContentClassifierOptionsQuery
} from '../../../api/client'
import FilterInput, { FilterItem } from './FilterInput'
import SearchParameters, { mapToContentSearchParams } from './searchParameters'

type Props = {
    searchParameters: SearchParameters
    onUpdateSelectedClassifiers: (classifiers: Array<string>) => void
}

export default function GeneralClassifierFilter(props: Props) {
    const { searchParameters, onUpdateSelectedClassifiers } = props
    const [searchTerm, setSearchTerm] = useState('')

    // The same mapper the feed itself uses, rather than a hand-rolled subset.
    //
    // The subset this replaced omitted excludeOwnComments, owned, readFilter,
    // hasMedia, adsOrOrganic and contentIds, so with any of those active the
    // count beside each option described a wider set of comments than the feed
    // was showing. That is a mismatch no amount of care in the backend can fix,
    // because the two queries were being asked different questions. — Claude
    const parameters: ContentSearchParams =
        mapToContentSearchParams(searchParameters)

    // The uncounted list, so the dropdown has something to show while the
    // counts are still being worked out. Same search scope as the counted query
    // — which classifiers flagged anything in this search — but without the
    // GROUP BY, so it is the cheaper of the two. Scoped rather than the whole
    // catalogue because the catalogue now holds every client's bespoke prompt
    // classifiers (EN-286), and offering those across clients would be both
    // confusing and a leak of their names. — Claude
    const { data: optionsData, loading: loadingOptions } = useGetContentClassifierOptionsQuery({
        variables: {
            params: parameters
        },
        onError: (e) => {
            // SOMEDAY : Use the actual logging solution
            console.error(`There was a problem getting the classifier list. ${e}`)
        }
    })

    const { data, loading } = useGetContentClassifierMetadataQuery({
        variables: {
            params: parameters
        },
        onError: (e) => {
            // SOMEDAY : Use the actual logging solution
            console.error(
                `There was a problem getting classification data - try reducing the date range. ${e}`
            )
        }
    })

    // The counts win as soon as they arrive; until then — and if the counted
    // query times out or errors, when Apollo leaves `data` undefined — the
    // uncounted list stands in. Both lists are taken over the same search, so
    // the swap only ever adds counts and re-ranks; nothing appears or vanishes. — Claude
    const counted = data?.getContentClassifierMetadata as
        | ContentClassifierMetadataView[]
        | undefined

    const contentClassifierMetadataItems: ContentClassifierMetadataView[] =
        counted ??
        ((optionsData?.getContentClassifierOptions || []).map((detail) => ({
            classifierId: detail.id,
            classifierName: detail.shortName,
            classifierDisplayName: detail.displayName,
            // No count yet. `mapClassifierToFilterItem` reads `counted` rather
            // than this, so nothing renders a placeholder "(0)". — Claude
            contentCount: 0
        })) as ContentClassifierMetadataView[])

    function handleUpdateSelectedClassifiers(selectedItems: Array<FilterItem>) {
        // @ts-ignore
        onUpdateSelectedClassifiers(selectedItems.map((item) => item.name))
    }

    /**
     * Maps between the Metadata item and the item supported by FilterInput
     */
    function mapClassifierToFilterItem(item: ContentClassifierMetadataView) {
        return {
            id: item.classifierId,
            name: `${item.classifierName}`,
            displayName: counted
                ? `${item.classifierDisplayName} (${item.contentCount})`
                : item.classifierDisplayName,
            rank: item.contentCount
        }
    }

    // Identify all the selected items from the search parameters.
    const selectedItems: FilterItem[] = contentClassifierMetadataItems
        .filter((item) => searchParameters.selectedGeneralClassifiers.includes(item.classifierName))
        .map(mapClassifierToFilterItem)

    const searchResults: FilterItem[] = contentClassifierMetadataItems
        // Only show classifier metadata that has matching content in the current
        // search — a rule that can only be applied once the counts are in.
        .filter((item) => !counted || item.contentCount > 0)
        // If there is a search term only show matching items.
        .filter((item: ContentClassifierMetadataView) =>
            item.classifierDisplayName.toLowerCase().includes(searchTerm.toLowerCase())
        )
        // Map to the object supported by the FilterInput.
        .map(mapClassifierToFilterItem)

    // Sort search results by rank, or alphabetically while every rank is zero.
    searchResults.sort((a, b) =>
        counted ? (b.rank || 0) - (a.rank || 0) : a.displayName.localeCompare(b.displayName)
    )

    /*
    This is a complex bit. If the search is across multiple accounts then each account might
    have its own classifier with the same display name but a different prompt, so the list
    holds several items with one name and picking one by name would be very confusing. — Claude
    To work around this we need to disable selection of these items and present the user with a
    message suggesting they change the account selection or talk to customer success.

    So the next stage is to identify duplicates in the list of FilterItems and mark them as disabled
    */
    const nameCountMap: { [key: string]: number } = {}

    // Count occurrences of each name in searchResults
    searchResults.forEach((item) => {
        if (item.name) {
            if (nameCountMap[item.name]) {
                nameCountMap[item.name]++
            } else {
                nameCountMap[item.name] = 1
            }
        }
    })

    // Mark items as disabled if they have duplicates
    searchResults.forEach((item) => {
        if (item.name) {
            if (nameCountMap[item.name] > 1) {
                item.disabled = true
                item.message =
                    'Unable to filter on this classifier. Please refine your profile selection or contact customer support.'
            }
        }
    })

    return (
        <FilterInput
            filterTypeName={`Classifiers`}
            searchTerm={searchTerm}
            onSearchTermChange={async (term) => {
                setSearchTerm(term)
            }}
            onUpdateSelectedItems={handleUpdateSelectedClassifiers}
            selectedItems={selectedItems}
            searchResults={searchResults}
            // Only the uncounted query blocks the list; the counts arrive under it.
            searching={loadingOptions && !counted}
            refreshing={loading}
            hideName={true}
        />
    )
}
